import { useEffect, useState } from 'react'
import { Role } from 'common/api/v1/types'
import { Api } from '../../../store'
import { useRoutes } from '../../../store'
import { useDebounce } from '../../../utils/hooks'
import { useUser } from '../../../utils'
import { useSettings } from '../../../queries/settings'
import type { Routes } from '../../../utils/routes'
import {
  PAGE_NAMES,
  CREATE_ACTIONS,
  filterCreateActions,
  isDialogAction,
  addApplianceAction,
} from '../../../utils/routes'
import type { GlobalSearchCategory, GlobalSearchResult } from './types'

const SUPER_ONLY: Set<keyof Routes> = new Set([
  'networks',
  'nmos',
  'addressMappings',
  'kubernetesNodes',
  'services',
  'alarmLogs',
  'billing',
  'globalSettings',
  'status',
])
const ADMIN_UP: Set<keyof Routes> = new Set([
  'overlays',
  'transformationProfiles',
  'usage',
  'adInsertion',
  'adServers',
  'adPods',
  'adCreatives',
])
const AD_PAGES: Set<keyof Routes> = new Set([
  'adInsertion',
  'adServers',
  'adPods',
  'adCreatives',
  'adBreaks',
  'adBreakControl',
  'adAudit',
])

const NAVIGATE_CATEGORY = 'Navigate'
const CREATE_CATEGORY = 'Create'

function searchPages(routes: Routes, term: string, userRole: Role, adInsertionEnabled: boolean): GlobalSearchResult[] {
  const lower = term.toLowerCase()
  return (Object.entries(PAGE_NAMES) as [keyof Routes, string][])
    .filter(([key, name]) => {
      if (SUPER_ONLY.has(key) && userRole !== Role.super) return false
      if (ADMIN_UP.has(key) && ![Role.admin, Role.super].includes(userRole)) return false
      if (AD_PAGES.has(key) && !adInsertionEnabled) return false
      return name.toLowerCase().includes(lower)
    })
    .map(([key, name]) => {
      const navigateTo = routes[key]()
      return { id: navigateTo, name, category: NAVIGATE_CATEGORY, navigateTo }
    })
}

function searchCreateItems(
  routes: Routes,
  term: string,
  userRole: Role,
  adInsertionEnabled: boolean,
): GlobalSearchResult[] {
  const lower = term.toLowerCase()
  return filterCreateActions(CREATE_ACTIONS, userRole, adInsertionEnabled)
    .filter((action) => `Create ${action.name}`.toLowerCase().includes(lower))
    .map((action) => {
      const navigateTo = isDialogAction(action) ? addApplianceAction(action.dialogType) : action.getRoute(routes)
      return {
        id: `create-${navigateTo}`,
        name: `Create ${action.name}`,
        category: CREATE_CATEGORY,
        navigateTo,
      }
    })
}

export const useGlobalSearch = (searchTerm: string) => {
  const [categories, setCategories] = useState<GlobalSearchCategory[]>([])
  const [loading, setLoading] = useState(false)
  const debouncedTerm = useDebounce(searchTerm, 300)
  const routes = useRoutes()
  const user = useUser()
  const settings = useSettings().data?.settings
  const expFeatures = settings?.expFeatures

  // Mark as loading immediately when the search term changes and is pending debounce
  const isPendingDebounce = searchTerm.length >= 2 && searchTerm !== debouncedTerm
  useEffect(() => {
    if (isPendingDebounce) setLoading(true)
  }, [isPendingDebounce])

  useEffect(() => {
    if (!debouncedTerm || debouncedTerm.length < 2) {
      setCategories([])
      setLoading(false)
      return
    }

    const controller = new AbortController()
    const { signal } = controller

    const fetchResults = async () => {
      setLoading(true)

      const params = { pageNumber: '0', rowsPerPage: '3', filter: debouncedTerm }

      interface SearchDef {
        category: string
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fetch: () => Promise<{ items: any[]; total: number }>
        getName: (item: any) => string
        getRoute: (item: any) => string
        getId: (item: any) => string
        getListRoute: () => string
      }

      const searches: SearchDef[] = [
        {
          category: 'Inputs',
          fetch: () => Api.inputApi.getBareInputs(params, { signal }),
          getName: (item) => item.name,
          getId: (item) => item.id,
          getRoute: (item) => routes.inputsUpdate({ id: item.id }),
          getListRoute: () => routes.inputs({ filter: debouncedTerm }),
        },
        {
          category: 'Input Lists',
          fetch: () => Api.inputListsApi.getInputLists(params, { signal }),
          getName: (item) => item.name,
          getId: (item) => item.id,
          getRoute: (item) => routes.inputListsUpdate({ id: item.id }),
          getListRoute: () => routes.inputLists({ filter: debouncedTerm }),
        },
        {
          category: 'Outputs',
          fetch: () =>
            Api.outputApi.listOutputs({ filter: { searchName: debouncedTerm }, skip: 0, limit: 3 }, { signal }),
          getName: (item) => item.name,
          getId: (item) => item.id,
          getRoute: (item) => routes.outputsUpdate({ id: item.id }),
          getListRoute: () => routes.outputs({ filter: debouncedTerm }),
        },
        {
          category: 'Appliances',
          fetch: () => Api.appliancesApi.getBareAppliances(params, { signal }),
          getName: (item) => item.name,
          getId: (item) => item.id,
          getRoute: (item) => routes.appliancesUpdate({ id: item.id }),
          getListRoute: () => routes.appliances({ filter: debouncedTerm }),
        },
        {
          category: 'Regions',
          fetch: () => Api.regionApi.getRegions(params, undefined, { signal }),
          getName: (item) => item.name,
          getId: (item) => item.id,
          getRoute: (item) => routes.regionsUpdate({ id: item.id }),
          getListRoute: () => routes.regions({ filter: debouncedTerm }),
        },
        {
          category: 'Groups',
          fetch: () => Api.groupsApi.getPureGroups(params, { signal }),
          getName: (item) => item.name,
          getId: (item) => item.id,
          getRoute: (item) => routes.groupsUpdate({ id: item.id }),
          getListRoute: () => routes.groups({ filter: debouncedTerm }),
        },
        {
          category: 'Users',
          fetch: () => Api.userApi.getUsers(params, false, { signal }),
          getName: (item) => item.username,
          getId: (item) => item.id,
          getRoute: (item) => routes.usersUpdate({ id: item.id }),
          getListRoute: () => routes.users({ filter: debouncedTerm }),
        },
        {
          category: 'Output Lists',
          fetch: () => Api.outputListsApi.getOutputLists(params, { signal }),
          getName: (item) => item.name,
          getId: (item) => item.id,
          getRoute: (item) => routes.outputListsUpdate({ id: item.id }),
          getListRoute: () => routes.outputLists({ filter: debouncedTerm }),
        },
        {
          category: 'Group Lists',
          fetch: () => Api.groupListsApi.getGroupLists(params, { signal }),
          getName: (item) => item.name,
          getId: (item) => item.id,
          getRoute: (item) => routes.groupListsUpdate({ id: item.id }),
          getListRoute: () => routes.groupLists({ filter: debouncedTerm }),
        },
        {
          category: 'Interfaces',
          fetch: () => Api.portsApi.getBarePorts(params, { signal }),
          getName: (item) => `${item.appliance.name}:${item.name}`,
          getId: (item) => item.id,
          getRoute: (item) => routes.interfacesUpdate({ id: item.id }),
          getListRoute: () => routes.interfaces({ filter: debouncedTerm }),
        },
        {
          category: 'Networks',
          fetch: () => Api.networksApi.getNetworks(params, { signal }),
          getName: (item) => item.name,
          getId: (item) => item.id,
          getRoute: (item) => routes.networkUpdate({ id: item.id }),
          getListRoute: () => routes.networks({ filter: debouncedTerm }),
        },
        {
          category: 'Overlays',
          fetch: () => Api.overlaysApi.listOverlays(params, { signal }),
          getName: (item) => item.name,
          getId: (item) => item.id ?? item.name,
          getRoute: (item) => routes.overlaysUpdate({ id: item.id }),
          getListRoute: () => routes.overlays({ filter: debouncedTerm }),
        },
        {
          category: 'Transformation Profiles',
          fetch: () => Api.transformationProfilesApi.listTransformationProfiles(params, { signal }),
          getName: (item) => item.name,
          getId: (item) => item.id,
          getRoute: (item) => routes.transformationProfilesUpdate({ id: item.id }),
          getListRoute: () => routes.transformationProfiles({ filter: debouncedTerm }),
        },
      ]

      if ([Role.super, Role.admin].includes(user.role) && expFeatures?.AdInsertion) {
        searches.push(
          {
            category: 'Ad Servers',
            fetch: () => Api.adServersApi.listAdServers(params, { signal }),
            getName: (item) => item.name,
            getId: (item) => item.id,
            getRoute: (item) => routes.adServersUpdate({ id: item.id }),
            getListRoute: () => routes.adServers({ filter: debouncedTerm }),
          },
          {
            category: 'Ad Pods',
            fetch: () => Api.adPodsApi.listAdPods(params, { signal }),
            getName: (item) => item.name,
            getId: (item) => item.id,
            getRoute: (item) => routes.adPodsUpdate({ id: item.id }),
            getListRoute: () => routes.adPods({ filter: debouncedTerm }),
          },
          {
            category: 'Ad Creatives',
            fetch: () => Api.adCreativesApi.listAdCreatives(params, { signal }),
            getName: (item) => item.name,
            getId: (item) => item.id,
            getRoute: (item) => routes.adCreativesUpdate({ id: item.id }),
            getListRoute: () => routes.adCreatives({ filter: debouncedTerm }),
          },
        )
      }

      if (user.role === Role.super) {
        searches.push(
          {
            category: 'Services',
            fetch: () => Api.serviceApi.getServices(params, { signal }),
            getName: (item) => item.purpose,
            getId: (item) => item.id,
            getRoute: (item) => routes.servicesUpdate({ id: item.id }),
            getListRoute: () => routes.services({ filter: debouncedTerm }),
          },
          {
            category: 'Kubernetes Nodes',
            fetch: () => Api.kubernetesApi.getKubernetesNodes(params, { signal }),
            getName: (item) => item.name,
            getId: (item) => item.name,
            getRoute: (item) => routes.kubernetesNodesUpdate({ regionId: item.region.id, name: item.name }),
            getListRoute: () => routes.kubernetesNodes({ filter: debouncedTerm }),
          },
        )
      }

      const results = await Promise.allSettled(searches.map((s) => s.fetch()))

      if (signal.aborted) return

      const isAdminUp = [Role.super, Role.admin].includes(user.role)
      const adInsertionEnabled = isAdminUp && !!expFeatures?.AdInsertion
      const pageResults = searchPages(routes, debouncedTerm, user.role, adInsertionEnabled)
      const newCategories: GlobalSearchCategory[] = []
      if (pageResults.length > 0) {
        newCategories.push({
          category: NAVIGATE_CATEGORY,
          results: pageResults,
          total: pageResults.length,
          navigateTo: '',
        })
      }
      if (isAdminUp) {
        const createResults = searchCreateItems(routes, debouncedTerm, user.role, adInsertionEnabled)
        if (createResults.length > 0) {
          newCategories.push({
            category: CREATE_CATEGORY,
            results: createResults,
            total: createResults.length,
            navigateTo: '',
          })
        }
      }
      results.forEach((result, index) => {
        if (result.status !== 'fulfilled') return
        const { items, total } = result.value
        if (items.length === 0) return

        const search = searches[index]
        const searchResults: GlobalSearchResult[] = items.map((item) => ({
          id: search.getId(item),
          name: search.getName(item),
          category: search.category,
          navigateTo: search.getRoute(item),
        }))

        newCategories.push({
          category: search.category,
          results: searchResults,
          total,
          navigateTo: search.getListRoute(),
        })
      })

      setCategories(newCategories)
      setLoading(false)
    }

    void fetchResults()

    return () => {
      controller.abort()
    }
  }, [debouncedTerm, routes, user.role, expFeatures?.AdInsertion])

  return { categories, loading }
}
